import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { backendServices } from 'common';
import { PostSignupFormData } from './steps';

export type AgreementStepKey = keyof PostSignupFormData;

// TODO: Remittance-only requests also run the TIN check, so Remittance needs Sovos there too.
const AGREEMENT_NAME_BY_STEP: Partial<Record<AgreementStepKey, string>> = {
    taxDetails: 'Sovos Service Agreement',
    remittance: 'Plaid Service Agreement',
};

const POST_SIGNUP_AGREEMENT_NAMES = Object.values(AGREEMENT_NAME_BY_STEP);

export type PostSignupAgreement = backendServices.ViewModels.CompanyServiceAgreementViewModel & {
    serviceAgreementContentId: number;
    agreementUrl: string | null;
};

function agreementsQueryKey(supplierCompanyId: number | null) {
    return ['PostSignupAgreements', supplierCompanyId];
}

// Plaid and Sovos keep their agreement link in HtmlFile.
function toAgreementUrl(htmlFile: string | null): string | null {
    try {
        const url = new URL((htmlFile ?? '').trim());
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
    } catch {
        return null;
    }
}

export function isSovosAgreementNeeded(tin: string | null | undefined): boolean {
    return Boolean(tin?.trim());
}

export function isPlaidAgreementNeeded(paymentMethod: string | null | undefined): boolean {
    return paymentMethod === 'ACH';
}

/** An agreement is needed only when the check it unlocks will run for these values. */
export function isAgreementNeeded(stepKey: AgreementStepKey, formData: PostSignupFormData): boolean {
    if (stepKey === 'taxDetails') {
        return isSovosAgreementNeeded(formData.taxDetails.TIN);
    }
    if (stepKey === 'remittance') {
        return isPlaidAgreementNeeded(formData.remittance.PreferredPaymentMethod);
    }
    return false;
}

/** The backend refuses ACH and TIN validation until these are signed. */
export function usePostSignupAgreements({
    supplierCompanyId,
    enabled = true,
}: {
    supplierCompanyId: number | null;
    enabled?: boolean;
}) {
    const queryClient = useQueryClient();

    const query = useQuery(
        agreementsQueryKey(supplierCompanyId),
        async (): Promise<PostSignupAgreement[]> => {
            const api = new backendServices.Apis.ServiceAgreementApi();
            const res = await api.getCompanyServiceAgreements(supplierCompanyId!);
            const rows = res.data.filter((row) =>
                POST_SIGNUP_AGREEMENT_NAMES.includes(row.serviceAgreement?.Name ?? '')
            );
            return Promise.all(
                rows.map(async (row) => {
                    const content = await api.getCurrentServiceAgreementContent(row.ServiceAgreementId);
                    return {
                        ...row,
                        serviceAgreementContentId: content.Id,
                        agreementUrl: toAgreementUrl(content.HtmlFile),
                    };
                })
            );
        },
        {
            enabled: enabled && supplierCompanyId != null,
            staleTime: Infinity,
            cacheTime: Infinity,
        }
    );

    const getStepAgreement = useCallback(
        (stepKey: AgreementStepKey | undefined) =>
            (query.data ?? []).find(
                (agreement) => stepKey != null && agreement.serviceAgreement?.Name === AGREEMENT_NAME_BY_STEP[stepKey]
            ),
        [query.data]
    );

    const recordSignature = useCallback(
        async (agreement: PostSignupAgreement) => {
            await new backendServices.Apis.ServiceAgreementApi().postCompanyServiceAgreementSignature({
                CompanyId: agreement.CompanyId,
                ServiceAgreementContentId: agreement.serviceAgreementContentId,
            });
            queryClient.invalidateQueries({ queryKey: agreementsQueryKey(supplierCompanyId) });
        },
        [queryClient, supplierCompanyId]
    );

    return {
        getStepAgreement,
        recordSignature,
        // First load only; the refetch after signing must not unmount the form.
        isLoading: query.isLoading,
        isError: query.isError,
    };
}
